/* 
 * Dialog with a title and close button
 * Defaults to content-pane modal; set scope="app" for full-app overlays
 */

/* global customElements */
import {LitElement, html} from 'lit';
import {BaseComponent} from '../basecomponent.js';
import {ElementUtil} from '../../utils/elementUtil.js';
import {pageRegistry} from '../../utils/pageRegistry.js';

class DialogWindow extends BaseComponent {

	static MOBILE_BREAKPOINT = 600;
	static DEFAULT_MOBILE_WIDTH = 0.9;
	
	static get properties() {
		return {
			// Not named "title": HTMLElement.title is the native browser tooltip attribute.
			headertext: {type: String, attribute: false},
			icon: {type: String},
			iconStyle: {type: String},
			notClosable: {type: Boolean},
			width: {type: Number},
			height: {type: Number},
			mobileWidth: {type: Number, attribute: 'mobile-width'},
			mobileHeight: {type: Number, attribute: 'mobile-height'},
			contentBackgroundColor: {type: String},
			backdropExtraStyle: {type: String},
			notModal: {type: Boolean},
			draggable: {type: Boolean},
			resizable: {type: Boolean},
			scope: {type: String},
			fillContent: {type: Boolean},
			// When true, height is treated as a max; content height shrinks to fit up to that max.
			fitContent: {type: Boolean}
		};
	}

	constructor() {
		super();
		this.headertext = '';
		this.icon = 'vaadin:modal-list';
		this.iconStyle = ''; 
		this.notClosable = false;	
		this.width = 0.8;
		this.height = 0.8;
		this.mobileWidth = null;
		this.mobileHeight = null;
		this.notModal = false;
		this.draggable = false;
		this.resizable = false;
		this.scope = 'content';
		this.fillContent = false;
		this.fitContent = false;
		this.contentBackgroundColor = window.DL.colorMode.background;
		this.backdropExtraStyle = '';
		this._contentScoped = false;
		this._fitContentFinalizeToken = 0;
		
		var dialogWindow = this;
		setTimeout(function(){
			dialogWindow.clearNativeTitleAttribute();
			dialogWindow.init();
		});
	}

	/**
	 * Keep ElementUtil.create({ title: '...' }) and dialog.title = '...' working
	 * without applying the native HTML title tooltip to the whole dialog.
	 */
	get title() {
		return this.headertext;
	}

	set title(value) {
		this.headertext = value == null ? '' : String(value);
		this.clearNativeTitleAttribute();
	}

	setAttribute(name, value) {
		if (name === 'title') {
			this.headertext = value == null ? '' : String(value);
			return;
		}
		super.setAttribute(name, value);
	}

	clearNativeTitleAttribute() {
		if (this.hasAttribute('title')) {
			HTMLElement.prototype.removeAttribute.call(this, 'title');
		}
	}

	connectedCallback() {
		super.connectedCallback();
		if (this.hasAttribute('title')) {
			this.headertext = this.getAttribute('title') || '';
			this.clearNativeTitleAttribute();
		}
	}

	render() {	
		return html`
			<style>
				.dialog-header {
					background-color: #383636;
					border-bottom: 3px solid ${this.getAccentColor()};
					color: white;
					padding: 5px 10px 3px 10px;
					box-sizing: border-box;
					display: flex;
					align-items: center;
					gap: 8px;
					width: 100%;
					min-height: 40px;
					overflow: hidden;
				}

				.dl-content-dialog-panel .dialog-header {
					height: auto;
					min-height: 40px;
				}

				.dialog-closebutton {
					flex: 0 0 auto;
					margin-left: auto;
					padding: 5px;
					background-color: #d40000;
					border-radius: 4px 4px 4px 4px;
				}
		
				.dialog-closebutton:hover {
					cursor: pointer;
					background-color: red;
					box-shadow: 0px 0px 5px red;
				}
	
				.title-text {
					flex: 1 1 auto;
					min-width: 0;
					font-size: 25px;
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
				}
		
				@media only screen and (max-width: 600px) {
					.title-text {
						font-size: 12px;
					}
					
					.dialog-closebutton {
						font-size: 10px;
						margin-top: 1px;
						padding: 0px;
					}		
				}
		
				.dialog-content {
					background-color: ${this.contentBackgroundColor};
					padding: 10px;
					overflow-y: auto;
					box-sizing: border-box;
				}

				.dialog-panel {
					box-sizing: border-box;
					max-width: 100%;
				}

				.dl-content-dialog-root {
					position: absolute;
					inset: 0;
					z-index: 30;
					pointer-events: auto;
					display: flex;
					align-items: center;
					justify-content: center;
				}

				.dl-content-dialog-backdrop {
					position: absolute;
					inset: 0;
					backdrop-filter: blur(2px);
					background-color: #13131399;
				}

				.dl-content-dialog-panel {
					position: relative;
					z-index: 1;
					box-shadow: 0 4px 24px rgba(0,0,0,0.35);
					max-width: 100%;
					max-height: 100%;
					border: 15px solid rgba(255, 255, 255, 0.5);
					border-radius: 5px;
					box-sizing: border-box;
				}
						
			</style>		
			<vaadin-dialog no-close-on-outside-click>				
			</vaadin-dialog>
		`;
	}
	
	createRenderRoot() {
		return this;	
	}

	resolveAttachTarget() {
		var dialogWindow = this;
		var useAppScope = dialogWindow.scope === 'app';
		if (!useAppScope) {
			var overlayHost = pageRegistry.getActiveOverlayHost();
			if (overlayHost) {
				dialogWindow._contentScoped = true;
				return overlayHost;
			}
		}
		dialogWindow._contentScoped = false;
		return document.body;
	}
	
	init() {
		var dialogWindow = this;
		var attachTarget = dialogWindow.resolveAttachTarget();
		attachTarget.append(dialogWindow);
		
		setTimeout(function(){
			if (dialogWindow._contentScoped) {
				dialogWindow.initContentScoped();
			} else {
				dialogWindow.initAppScoped();
			}
		});
	}

	buildHeaderAndContent() {
		var dialogWindow = this;
		var headerChildren = [
			{
				tag: 'vaadin-icon',
				icon: dialogWindow.icon,
				style: 'margin-top: -10px;' + dialogWindow.iconStyle
			}, 
			{
				tag: 'span',
				'class': 'title-text',
				innerHTML: ' ' + dialogWindow.headertext
			}
		];
		if (!dialogWindow.notClosable) {
			headerChildren.push({
				tag: 'div',
				'class': 'dialog-closebutton',
				'aria-label': 'close',					
				listeners: {
					click: function() {
						dialogWindow.close();
					}
				},
				children: [
					{
						tag: 'vaadin-icon',
						icon: 'lumo:cross',
						slot: 'prefix'
					}
				]
			});
		}
		
		dialogWindow.headerNode = ElementUtil.create({
			tag: 'div',
			'class': 'dialog-header',
			children: headerChildren
		});				
		
		dialogWindow.contentNode = ElementUtil.create({
			tag: 'div',
			'class': 'dialog-content'
		});

		dialogWindow.panelNode = ElementUtil.create({
			tag: 'div',
			'class': 'dialog-panel',
			children: [
				dialogWindow.headerNode,
				dialogWindow.contentNode
			]
		});

		dialogWindow.frameNode = ElementUtil.create({
			tag: 'div',
			'class': 'dl-content-dialog-panel',
			children: [
				dialogWindow.panelNode
			]
		});
	}

	applyDialogDimensions(viewportWidth, viewportHeight, headerOffset) {
		var dialogWindow = this;
		var newWidth = dialogWindow.resolveDialogWidth(viewportWidth);
		var maxHeight = dialogWindow.resolveDialogHeight(viewportHeight, headerOffset, viewportWidth);
		if (!dialogWindow.fitContent) {
			dialogWindow.applyDialogDimensionsPx(newWidth, maxHeight);
			return;
		}
		dialogWindow.applyFitContentDimensions(newWidth, maxHeight);
	}

	/**
	 * Re-measure and apply content height when fitContent is enabled (e.g. after show/hide of children).
	 * No-op when fitContent is false; callers may still invoke adjustContentSize for fixed sizing.
	 */
	refitToContent() {
		var dialogWindow = this;
		if (dialogWindow.adjustContentSize) {
			dialogWindow.adjustContentSize();
		}
	}

	applyFitContentDimensions(newWidth, maxHeight) {
		var dialogWindow = this;
		var widthPx = Math.round(newWidth);
		var maxHeightPx = Math.round(maxHeight);
		dialogWindow.applyDialogPanelWidth(widthPx);
		dialogWindow.applyDialogContentStyle(widthPx, null, maxHeightPx);
		var finalizeToken = ++dialogWindow._fitContentFinalizeToken;
		requestAnimationFrame(function() {
			requestAnimationFrame(function() {
				if (finalizeToken !== dialogWindow._fitContentFinalizeToken) {
					return;
				}
				var measuredHeight = dialogWindow.measureContentHeight();
				var finalHeight = Math.min(Math.max(measuredHeight, 80), maxHeightPx);
				dialogWindow.applyDialogContentStyle(widthPx, finalHeight, maxHeightPx);
			});
		});
	}

	measureContentHeight() {
		var dialogWindow = this;
		if (!dialogWindow.contentNode) {
			return 120;
		}
		var contentNode = dialogWindow.contentNode;
		var measured = contentNode.scrollHeight;
		var children = contentNode.children;
		var childTotal = 0;
		for (var index = 0; index < children.length; index++) {
			var child = children[index];
			if (child.classList && child.classList.contains('hide')) {
				continue;
			}
			var computed = window.getComputedStyle(child);
			if (computed.display === 'none' || computed.visibility === 'hidden') {
				continue;
			}
			childTotal += child.offsetHeight;
		}
		if (childTotal > measured) {
			measured = childTotal;
		}
		return measured;
	}

	applyDialogPanelWidth(widthPx) {
		var dialogWindow = this;
		var panelStyle = 'width: ' + widthPx + 'px; max-width: 100%; box-sizing: border-box;';
		if (dialogWindow.panelNode) {
			dialogWindow.panelNode.setAttribute('style', panelStyle);
		} else if (dialogWindow.headerNode) {
			dialogWindow.headerNode.setAttribute('style', panelStyle);
		}
	}

	applyDialogContentStyle(widthPx, heightPx, maxHeightPx) {
		var dialogWindow = this;
		if (!dialogWindow.contentNode) {
			return;
		}
		var contentStyle = 'width: ' + widthPx + 'px; max-width: 100%; box-sizing: border-box;';
		if (heightPx == null) {
			contentStyle += ' height: auto;';
			if (maxHeightPx != null) {
				contentStyle += ' max-height: ' + Math.round(maxHeightPx) + 'px;';
			}
		} else {
			contentStyle += ' height: ' + Math.round(heightPx) + 'px;';
			if (maxHeightPx != null) {
				contentStyle += ' max-height: ' + Math.round(maxHeightPx) + 'px;';
			}
		}
		// Keep overflow scrollable by default; fillContent opts into clipping (e.g. embedded grids).
		if (dialogWindow.fillContent) {
			contentStyle += ' overflow: hidden; padding: 0px;';
		} else {
			contentStyle += ' overflow-y: auto;';
		}
		dialogWindow.contentNode.setAttribute('style', contentStyle);
	}

	applyDialogDimensionsPx(newWidth, newHeight) {
		var dialogWindow = this;
		var widthPx = Math.round(newWidth);
		var heightPx = Math.round(newHeight);
		dialogWindow.applyDialogPanelWidth(widthPx);
		dialogWindow.applyDialogContentStyle(widthPx, heightPx, null);
	}

	parseSizeValue(value) {
		if (value == null || value === '') {
			return null;
		}
		return Number(value);
	}

	isMobileViewport(viewportWidth) {
		return viewportWidth <= DialogWindow.MOBILE_BREAKPOINT;
	}

	resolveDialogWidth(viewportWidth) {
		var dialogWindow = this;
		var widthValue = dialogWindow.parseSizeValue(dialogWindow.width);
		var isMobile = dialogWindow.isMobileViewport(viewportWidth);

		if (isMobile) {
			var mobileWidth = dialogWindow.parseSizeValue(dialogWindow.mobileWidth);
			if (mobileWidth != null) {
				widthValue = mobileWidth;
			} else if (widthValue != null && widthValue <= 1 && widthValue < DialogWindow.DEFAULT_MOBILE_WIDTH) {
				widthValue = DialogWindow.DEFAULT_MOBILE_WIDTH;
			}
		}

		var resolvedWidth = widthValue;
		if (widthValue != null && widthValue <= 1) {
			resolvedWidth = viewportWidth * widthValue;
		}
		return Math.min(resolvedWidth, viewportWidth - 32);
	}

	resolveDialogHeight(viewportHeight, headerOffset, viewportWidth) {
		var dialogWindow = this;
		var heightValue = dialogWindow.parseSizeValue(dialogWindow.height);
		var isMobile = dialogWindow.isMobileViewport(viewportWidth);

		if (isMobile) {
			var mobileHeight = dialogWindow.parseSizeValue(dialogWindow.mobileHeight);
			if (mobileHeight != null) {
				heightValue = mobileHeight;
			}
		}

		var resolvedHeight = heightValue;
		if (heightValue != null && heightValue <= 1) {
			resolvedHeight = (viewportHeight * heightValue) - headerOffset;
		}
		return Math.max(resolvedHeight, 120);
	}

	applyAppScopedOverlayStyles() {
		var dialogWindow = this;
		var dialog = dialogWindow.querySelector('vaadin-dialog');
		var overlay = dialog && dialog._overlayElement;
		if (!overlay || !overlay.shadowRoot) {
			return;
		}

		var backdrop = overlay.shadowRoot.querySelector('#backdrop, [part="backdrop"]');
		if (backdrop) {
			backdrop.style.cssText = 'backdrop-filter: blur(2px); ' + dialogWindow.backdropExtraStyle;
		}

		var overlayPanel = overlay.shadowRoot.querySelector('#overlay, [part="overlay"]');
		if (overlayPanel) {
			overlayPanel.style.backgroundColor = 'transparent';
			overlayPanel.style.boxShadow = 'none';
		}

		var overlayContent = overlay.shadowRoot.querySelector('[part="content"]');
		if (overlayContent) {
			overlayContent.style.padding = '0';
		}
	}

	initAppScoped() {
		var dialogWindow = this;
		var dialog = dialogWindow.querySelector('vaadin-dialog');			
					
		dialog.renderer = function(root, dialogLocal) {
			// Vaadin may invoke the renderer more than once; reuse the same frame so
			// content appended via addToContentElement is not orphaned on re-render.
			if (!dialogWindow.frameNode) {
				dialogWindow.buildHeaderAndContent();
			}
			root.replaceChildren(dialogWindow.frameNode);
		};		
	
		dialog.addEventListener('opened-changed', function(evt){
			if (evt.detail.value === true) {
				requestAnimationFrame(function() {
					dialogWindow.applyAppScopedOverlayStyles();
				});
			} else if (evt.detail.value === false) {
				var beforeClose = new CustomEvent('beforeclose', { detail: '' });
				var proceed = dialogWindow.dispatchEvent(beforeClose);
				if (proceed) {
					dialogWindow.remove();
				}
			}
		});
		var readyEvent = new CustomEvent('ready', { detail: '' });
		dialogWindow.dispatchEvent(readyEvent);
	}

	initContentScoped() {
		var dialogWindow = this;
		var vaadinDialog = dialogWindow.querySelector('vaadin-dialog');
		if (vaadinDialog) {
			vaadinDialog.remove();
		}

		dialogWindow.buildHeaderAndContent();

		dialogWindow.contentRoot = ElementUtil.create({
			tag: 'div',
			'class': 'dl-content-dialog-root',
			style: 'display: none;',
			children: [
				{
					tag: 'div',
					'class': 'dl-content-dialog-backdrop',
					style: dialogWindow.backdropExtraStyle || ''
				},
				dialogWindow.frameNode
			]
		});

		if (!dialogWindow.notModal) {
			dialogWindow.contentRoot.querySelector('.dl-content-dialog-backdrop').addEventListener('click', function(event) {
				event.stopPropagation();
			});
		}

		dialogWindow.append(dialogWindow.contentRoot);

		var readyEvent = new CustomEvent('ready', { detail: '' });
		dialogWindow.dispatchEvent(readyEvent);
	}
	
	show() {
		var dialogWindow = this;
		if (dialogWindow._contentScoped) {
			dialogWindow.showContentScoped();
			return;
		}

		var dialog = dialogWindow.querySelector('vaadin-dialog');
		if (dialogWindow.notModal) {
			dialog.setAttribute('modeless', true);
		}
		if (dialogWindow.draggable) {
			dialog.setAttribute('draggable', true);
		}
		if (dialogWindow.resizable) {
			dialog.setAttribute('resizable', true);
		}	
		if (dialogWindow.notClosable) {
			dialog.setAttribute('no-close-on-esc', true);
		}		
		
		dialog.opened = true;
		dialogWindow.adjustContentSize = function(){
			dialogWindow.applyDialogDimensions(window.innerWidth, window.innerHeight, 100);
		};
		dialogWindow._resizeHandler = function(){
			dialogWindow.adjustContentSize();
		};
		window.addEventListener('resize', dialogWindow._resizeHandler);

		requestAnimationFrame(function() {
			dialogWindow.applyAppScopedOverlayStyles();
			dialogWindow.adjustContentSize();
		});
	}

	showContentScoped() {
		var dialogWindow = this;
		if (!dialogWindow.contentRoot) {
			return;
		}
		dialogWindow.contentRoot.classList.remove('hide');
		dialogWindow.contentRoot.style.display = 'flex';

		var overlayHost = dialogWindow.parentElement;

		dialogWindow.adjustContentSize = function(){
			var hostWidth = overlayHost ? overlayHost.clientWidth : window.innerWidth;
			var hostHeight = overlayHost ? overlayHost.clientHeight : window.innerHeight;
			dialogWindow.applyDialogDimensions(hostWidth, hostHeight, 80);
		};
		dialogWindow.adjustContentSize();

		dialogWindow._escHandler = function(event) {
			if (event.key === 'Escape' && !dialogWindow.notClosable) {
				dialogWindow.close();
			}
		};
		window.addEventListener('keydown', dialogWindow._escHandler);
	}
	
	addToContentElement(element) {
		var dialogWindow = this;
		var content = dialogWindow.contentNode;
		content.append(element);
	}
	
	getContentElement() {
		var dialogWindow = this;
		return dialogWindow.contentNode;
	}
	
	close() {
		var dialogWindow = this;
		var beforeClose = new CustomEvent('beforeclose', { detail: '', cancelable: true });
		var proceed = dialogWindow.dispatchEvent(beforeClose);
		if (!proceed) {
			return;
		}

		if (dialogWindow._contentScoped) {
			if (dialogWindow._escHandler) {
				window.removeEventListener('keydown', dialogWindow._escHandler);
			}
			dialogWindow.remove();
			return;
		}

		if (dialogWindow._resizeHandler) {
			window.removeEventListener('resize', dialogWindow._resizeHandler);
		}

		var dialog = dialogWindow.querySelector('vaadin-dialog');
		if (dialog) {
			dialog.opened = false;
		} else {
			dialogWindow.remove();
		}
	}
	
	getAccentColor() {
		return window.DL.Branding.navBackgroundColor;	
	}
	
}

customElements.define('dl-dialog', DialogWindow);
